// Guide §10.2–10.4 — sync envelope and protocol DTOs.
import { z } from 'zod';
import { OP_TYPES, type OpType } from './schemas';
import { MODULE_ENTITIES } from '../modules/spec';
import { zId, zIso } from './schemas';
import { SYNC_MAX_OPS_PER_PUSH } from '../constants';

export const zActor = z.object({ type: z.enum(['owner', 'staff']), id: zId });
export type Actor = z.infer<typeof zActor>;

export const zSyncOp = z.object({
  opId: zId,
  deviceSeq: z.number().int().nonnegative(),
  shopId: zId,
  type: z.enum(OP_TYPES as [OpType, ...OpType[]]),
  entityId: zId,
  payload: z.unknown(),
  baseVersion: z.number().int().nonnegative().optional(),
  changedFields: z.array(z.string().max(60)).max(50).optional(),
  clientTs: zIso,
  actor: zActor,
  overrideToken: z.string().max(200).optional(),
  appVersion: z.string().max(20),
});
export type SyncOp = Omit<z.infer<typeof zSyncOp>, 'payload'> & { payload: unknown };

export const zPushRequest = z.object({
  shopId: zId,
  deviceId: zId,
  ops: z.array(zSyncOp).min(1).max(SYNC_MAX_OPS_PER_PUSH),
});
export type PushRequest = z.infer<typeof zPushRequest>;

export type OpStatus = 'applied' | 'duplicate' | 'rejected' | 'quarantined';
export interface OpResult {
  opId: string;
  status: OpStatus;
  serverSeq?: number;
  reason?: string;
  /** Extra per-op info the app may need, e.g. { calcMismatch: true } */
  info?: Record<string, unknown>;
}
export interface PushResponse {
  results: OpResult[];
  serverTime: string;
  /** Changes when the server database is restored from backup (Guide §21.3) */
  serverEpoch?: string;
}

export interface PullChange {
  seq: number;
  entity: string;
  entityId: string;
  op: 'upsert' | 'delete';
  data: unknown;
}
export interface PullResponse {
  changes: PullChange[];
  nextAfter: number;
  hasMore: boolean;
  serverTime: string;
  serverEpoch?: string;
}

/** Snapshot order for a new device (Guide §10.4) */
export const SNAPSHOT_ENTITIES: readonly string[] = [
  'settings',
  'money_accounts',
  'products',
  'product_units',
  'product_variants',
  'price_tiers',
  'stock_batches',
  'serial_units',
  'customers',
  'vendors',
  'sales',
  'sale_items',
  'sale_payments',
  'sale_returns',
  'sale_return_items',
  'customer_ledger',
  'installment_plans',
  'installments',
  'purchases',
  'purchase_items',
  'vendor_payments',
  'vendor_returns',
  'vendor_return_items',
  'vendor_ledger',
  'expenses',
  'expense_categories',
  'money_movements',
  'cash_closings',
  'stock_movements',
  'promotions',
  'notes',
  'repair_jobs',
  'service_rates',
  'bundles',
  'field_defs',
  'insights',
  // category modules (R1.1 / R1.2) — generated from modules/spec.ts
  ...MODULE_ENTITIES.map((e) => e.entity),
] as const;
export type SnapshotEntity = string;

/** Result of an op that needs an Owner decision (quarantine, Bible 2.10) */
export const QUARANTINE_REASONS = [
  'staff_deactivated',
  'role_not_allowed',
  'discount_over_limit',
  'plan_lapsed_premium',
  'serial_already_sold',
  'missing_reference',
] as const;
export type QuarantineReason = (typeof QUARANTINE_REASONS)[number];
